<template>
  <div class="meetings-page">
    <!-- Page Header -->
    <div class="d-flex justify-space-between align-center mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold text-primary mb-2">
          Board Meetings
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          Manage and track all board meetings, attendance, and minutes
        </p>
      </div>
      
      <div class="d-flex gap-2">
        <v-btn
          v-if="isAdmin"
          color="info"
          variant="outlined"
          prepend-icon="mdi-cog"
          @click="showMeetingTypesDialog = true"
        >
          Manage Types
        </v-btn>
        <v-btn
          color="secondary"
          variant="outlined"
          prepend-icon="mdi-calendar"
          @click="$router.push('/meetings/calendar')"
        >
          Calendar View
        </v-btn>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="$router.push('/meetings/create')"
        >
          New Meeting
        </v-btn>
      </div>
    </div>

    <!-- Filters and Search -->
    <v-card class="mb-6" flat>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              label="Search meetings..."
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Status"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="typeFilter"
              :items="meetingTypes"
              item-title="name"
              item-value="id"
              label="Meeting Type"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              variant="outlined"
              block
              @click="clearFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Meetings Table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="filteredMeetings"
        :loading="loading"
        :search="searchQuery"
        item-value="id"
        class="elevation-1"
        hover
      >
        <!-- Title with clickable link -->
        <template #item.title="{ item }">
          <router-link
            :to="`/meetings/${item.id}`"
            class="text-decoration-none text-primary font-weight-medium"
          >
            {{ item.title }}
          </router-link>
        </template>

        <!-- Meeting Type with color chip -->
        <template #item.type="{ item }">
          <v-chip
            :color="item.meeting_type?.color || 'primary'"
            variant="tonal"
            size="small"
          >
            {{ item.meeting_type?.name || 'Standard' }}
          </v-chip>
        </template>

        <!-- Date and Time -->
        <!-- Date and Time -->
        <template #item.datetime="{ item }">
          <div class="d-flex flex-column">
            <div class="font-weight-medium">
              {{ formatDate(item.start_datetime) }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ formatTime(item.start_datetime) }}
            </div>
          </div>
        </template>        <!-- Status with colored chips -->
        <template #item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            variant="tonal"
            size="small"
          >
            <v-icon start size="small">
              {{ getStatusIcon(item.status) }}
            </v-icon>
            {{ item.status.replace('_', ' ').toUpperCase() }}
          </v-chip>
        </template>

        <!-- Attendance -->
        <template #item.attendance="{ item }">
          <div class="d-flex align-center">
            <v-icon color="success" size="small" class="mr-1">
              mdi-account-check
            </v-icon>
            <span class="text-caption">
              {{ item.attendance_count || 0 }} / {{ item.expected_attendees || 0 }}
            </span>
          </div>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex">
            <v-tooltip text="View Details">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="small"
                  variant="text"
                  @click="viewMeeting(item.id)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip text="Edit Meeting">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="small"
                  variant="text"
                  @click="editMeeting(item.id)"
                  :disabled="item.status === 'completed'"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="small"
                  variant="text"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list density="compact">
                <v-list-item
                  v-if="item.status === 'scheduled'"
                  @click="startMeeting(item)"
                  prepend-icon="mdi-play"
                >
                  <v-list-item-title>Start Meeting</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="item.status === 'ongoing'"
                  @click="endMeeting(item)"
                  prepend-icon="mdi-stop"
                >
                  <v-list-item-title>End Meeting</v-list-item-title>
                </v-list-item>

                <v-list-item
                  @click="duplicateMeeting(item)"
                  prepend-icon="mdi-content-copy"
                >
                  <v-list-item-title>Duplicate</v-list-item-title>
                </v-list-item>

                <v-divider />

                <v-list-item
                  @click="cancelMeeting(item)"
                  prepend-icon="mdi-cancel"
                  :disabled="item.status === 'completed'"
                >
                  <v-list-item-title>Cancel Meeting</v-list-item-title>
                </v-list-item>

                <v-list-item
                  @click="deleteMeeting(item)"
                  prepend-icon="mdi-delete"
                  class="text-error"
                  :disabled="item.status === 'completed'"
                >
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>

        <!-- Empty state -->
        <template #no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey" class="mb-4">
              mdi-calendar-blank
            </v-icon>
            <h3 class="text-h6 text-medium-emphasis mb-2">
              No meetings found
            </h3>
            <p class="text-body-2 text-medium-emphasis mb-4">
              {{ searchQuery ? 'Try adjusting your search criteria' : 'Get started by creating your first meeting' }}
            </p>
            <v-btn
              v-if="!searchQuery"
              color="primary"
              @click="$router.push('/meetings/create')"
            >
              Create First Meeting
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Meeting Status Update Dialog -->
    <v-dialog v-model="statusDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          {{ statusDialog.title }}
        </v-card-title>
        <v-card-text>
          <p>{{ statusDialog.message }}</p>
          <v-textarea
            v-if="statusDialog.type === 'end'"
            v-model="statusDialog.notes"
            label="Meeting Notes (Optional)"
            placeholder="Add any important notes about the meeting..."
            rows="3"
            variant="outlined"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="statusDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            :color="statusDialog.type === 'cancel' ? 'error' : 'primary'"
            @click="confirmStatusUpdate"
          >
            {{ statusDialog.confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400">
      <v-card>
        <v-card-title class="text-h6 text-error">
          Delete Meeting
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ deleteDialog.meeting?.title }}"? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Meeting Types Management Dialog -->
    <v-dialog v-model="showMeetingTypesDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Manage Meeting Types</span>
          <v-btn
            icon
            variant="text"
            @click="showMeetingTypesDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Add New Meeting Type Form -->
          <v-card class="mb-6" flat border>
            <v-card-title class="text-h6">Add New Meeting Type</v-card-title>
            <v-card-text>
              <v-form ref="meetingTypeForm" v-model="meetingTypeFormValid" @submit.prevent="addMeetingType">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="newMeetingType.name"
                      label="Type Name *"
                      :rules="nameRules"
                      variant="outlined"
                      density="compact"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="newMeetingType.color"
                      label="Color"
                      type="color"
                      variant="outlined"
                      density="compact"
                      style="max-width: 120px;"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn
                      type="submit"
                      color="primary"
                      :disabled="!meetingTypeFormValid"
                      :loading="addingMeetingType"
                    >
                      Add Type
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="newMeetingType.description"
                      label="Description (Optional)"
                      variant="outlined"
                      density="compact"
                      rows="2"
                      auto-grow
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- Existing Meeting Types List -->
          <v-card flat border>
            <v-card-title class="text-h6">Existing Meeting Types</v-card-title>
            <v-card-text>
              <v-list>
                <v-list-item
                  v-for="type in meetingTypes"
                  :key="type.id"
                  class="meeting-type-item"
                >
                  <template #prepend>
                    <div
                      class="meeting-type-color"
                      :style="{ backgroundColor: type.color || '#1565C0' }"
                    ></div>
                  </template>

                  <v-list-item-title>{{ type.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="type.description">
                    {{ type.description }}
                  </v-list-item-subtitle>

                  <template #append>
                    <div class="d-flex align-center gap-1">
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click="editMeetingType(type)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="error"
                        @click="deleteMeetingType(type)"
                        :disabled="type.id <= 4"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="showMeetingTypesDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Meeting Type Dialog -->
    <v-dialog v-model="editTypeDialog.show" max-width="500">
      <v-card>
        <v-card-title>Edit Meeting Type</v-card-title>
        <v-card-text>
          <v-form ref="editTypeForm" v-model="editTypeFormValid" @submit.prevent="updateMeetingType">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editTypeDialog.type.name"
                  label="Type Name *"
                  :rules="nameRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editTypeDialog.type.color"
                  label="Color"
                  type="color"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editTypeDialog.type.description"
                  label="Description"
                  variant="outlined"
                  rows="2"
                  auto-grow
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="editTypeDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!editTypeFormValid"
            :loading="updatingMeetingType"
            @click="updateMeetingType"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Meeting Type Confirmation -->
    <v-dialog v-model="deleteTypeDialog.show" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Meeting Type</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ deleteTypeDialog.type?.name }}"? 
          This action cannot be undone and may affect existing meetings.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteTypeDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deletingMeetingType"
            @click="confirmDeleteMeetingType"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotifications } from '@/composables/useNotifications'
import { dateFormat } from '@/utils/dateFormat'
import api from '@/services/api'

export default {
  name: 'Meetings',
  setup() {
    const router = useRouter()
    const { 
      showSuccess, 
      showError, 
      showMeetingNotification,
      showOperationError 
    } = useNotifications()

    // Reactive data
    const loading = ref(false)
    const meetings = ref([])
    const meetingTypes = ref([])
    const searchQuery = ref('')
    const statusFilter = ref('')
    const typeFilter = ref('')

    // Dialog states
    const statusDialog = ref({
      show: false,
      type: '',
      meeting: null,
      title: '',
      message: '',
      confirmText: '',
      notes: ''
    })

    const deleteDialog = ref({
      show: false,
      meeting: null
    })

    // Meeting Types Management
    const isAdmin = ref(true) // TODO: Get from user auth state
    const showMeetingTypesDialog = ref(false)
    const meetingTypeFormValid = ref(false)
    const editTypeFormValid = ref(false)
    const addingMeetingType = ref(false)
    const updatingMeetingType = ref(false)
    const deletingMeetingType = ref(false)

    const newMeetingType = ref({
      name: '',
      description: '',
      color: '#1565C0'
    })

    const editTypeDialog = ref({
      show: false,
      type: {
        id: null,
        name: '',
        description: '',
        color: '#1565C0'
      }
    })

    const deleteTypeDialog = ref({
      show: false,
      type: null
    })

    // Validation rules
    const nameRules = [
      v => !!v || 'Name is required',
      v => v.length >= 2 || 'Name must be at least 2 characters',
      v => v.length <= 50 || 'Name must be less than 50 characters'
    ]

    // Table headers
    const headers = [
      { title: 'Meeting Title', key: 'title', sortable: true },
      { title: 'Type', key: 'type', sortable: false },
      { title: 'Date & Time', key: 'datetime', sortable: true },
      { title: 'Status', key: 'status', sortable: true },
      { title: 'Attendance', key: 'attendance', sortable: false },
      { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
    ]

    // Status options for filter
    const statusOptions = [
      { title: 'Scheduled', value: 'scheduled' },
      { title: 'Ongoing', value: 'ongoing' },
      { title: 'Completed', value: 'completed' },
      { title: 'Cancelled', value: 'cancelled' }
    ]

    // Computed
    const filteredMeetings = computed(() => {
      let filtered = meetings.value

      if (statusFilter.value) {
        filtered = filtered.filter(meeting => meeting.status === statusFilter.value)
      }

      if (typeFilter.value) {
        filtered = filtered.filter(meeting => meeting.meeting_type_id === typeFilter.value)
      }

      return filtered
    })

    // Methods
    const fetchMeetings = async () => {
      loading.value = true
      try {
        const response = await api.get('/meetings/')
        meetings.value = response.data.meetings || []
      } catch (error) {
        console.error('Error fetching meetings:', error)
        showError('Failed to load meetings')
      } finally {
        loading.value = false
      }
    }

    const fetchMeetingTypes = async () => {
      try {
        const response = await api.get('/meetings/types')
        meetingTypes.value = response.data.meeting_types || []
      } catch (error) {
        console.error('Error fetching meeting types:', error)
      }
    }

    const clearFilters = () => {
      searchQuery.value = ''
      statusFilter.value = ''
      typeFilter.value = ''
    }

    const formatDate = (dateString) => {
      return dateFormat.formatDate(dateString)
    }

    const formatTime = (dateString) => {
      return dateFormat.formatTime(dateString)
    }

    const formatDateTime = (dateString) => {
      return dateFormat.formatDateTime(dateString)
    }

    const formatRelativeTime = (dateString) => {
      return dateFormat.formatRelativeTime(dateString)
    }

    const getStatusColor = (status) => {
      const colors = {
        scheduled: 'primary',
        ongoing: 'warning',
        completed: 'success',
        cancelled: 'error'
      }
      return colors[status] || 'grey'
    }

    const getStatusIcon = (status) => {
      const icons = {
        scheduled: 'mdi-calendar-clock',
        ongoing: 'mdi-play-circle',
        completed: 'mdi-check-circle',
        cancelled: 'mdi-cancel'
      }
      return icons[status] || 'mdi-circle'
    }

    const viewMeeting = (meetingId) => {
      router.push(`/meetings/${meetingId}`)
    }

    const editMeeting = (meetingId) => {
      router.push(`/meetings/${meetingId}/edit`)
    }

    const startMeeting = (meeting) => {
      statusDialog.value = {
        show: true,
        type: 'start',
        meeting,
        title: 'Start Meeting',
        message: `Are you sure you want to start "${meeting.title}"?`,
        confirmText: 'Start Meeting',
        notes: ''
      }
    }

    const endMeeting = (meeting) => {
      statusDialog.value = {
        show: true,
        type: 'end',
        meeting,
        title: 'End Meeting',
        message: `Mark "${meeting.title}" as completed?`,
        confirmText: 'End Meeting',
        notes: ''
      }
    }

    const cancelMeeting = (meeting) => {
      statusDialog.value = {
        show: true,
        type: 'cancel',
        meeting,
        title: 'Cancel Meeting',
        message: `Are you sure you want to cancel "${meeting.title}"?`,
        confirmText: 'Cancel Meeting',
        notes: ''
      }
    }

    const confirmStatusUpdate = async () => {
      const { type, meeting, notes } = statusDialog.value
      
      try {
        let newStatus = type === 'start' ? 'ongoing' : 
                       type === 'end' ? 'completed' : 'cancelled'
        
        // Use the correct API endpoint - PUT /meetings/{id} with status in body
        await api.put(`/meetings/${meeting.id}`, {
          status: newStatus,
          minutes: notes || undefined
        })

        // Update local data
        const index = meetings.value.findIndex(m => m.id === meeting.id)
        if (index !== -1) {
          meetings.value[index].status = newStatus
        }

        const actionText = type === 'start' ? 'started' : type === 'end' ? 'completed' : 'cancelled'
        showMeetingNotification(actionText, statusDialog.value.meeting?.title, formatDateTime(statusDialog.value.meeting?.start_datetime))
        statusDialog.value.show = false
        
        // Refresh meetings to get updated data
        await fetchMeetings()
      } catch (error) {
        console.error('Error updating meeting status:', error)
        showOperationError('update meeting status', error)
      }
    }

    const duplicateMeeting = (meeting) => {
      router.push({
        path: '/meetings/create',
        query: { duplicate: meeting.id }
      })
    }

    const deleteMeeting = (meeting) => {
      deleteDialog.value = {
        show: true,
        meeting
      }
    }

    const confirmDelete = async () => {
      try {
        await api.delete(`/meetings/${deleteDialog.value.meeting.id}`)
        
        // Remove from local data
        meetings.value = meetings.value.filter(m => m.id !== deleteDialog.value.meeting.id)
        
        showMeetingNotification('deleted', deleteDialog.value.meeting?.title)
        deleteDialog.value.show = false
      } catch (error) {
        console.error('Error deleting meeting:', error)
        showError('Failed to delete meeting')
      }
    }

    // Meeting Types Management Methods
    const addMeetingType = async () => {
      if (!meetingTypeFormValid.value) return

      addingMeetingType.value = true
      try {
        const response = await api.post('/meetings/types', newMeetingType.value)
        
        // Add to local data
        meetingTypes.value.push(response.data.meeting_type)
        
        // Reset form
        newMeetingType.value = {
          name: '',
          description: '',
          color: '#1565C0'
        }
        
        showSuccess('Meeting type added successfully')
      } catch (error) {
        console.error('Error adding meeting type:', error)
        showError('Failed to add meeting type')
      } finally {
        addingMeetingType.value = false
      }
    }

    const editMeetingType = (type) => {
      editTypeDialog.value = {
        show: true,
        type: { ...type }
      }
    }

    const updateMeetingType = async () => {
      if (!editTypeFormValid.value) return

      updatingMeetingType.value = true
      try {
        const response = await api.put(`/meetings/types/${editTypeDialog.value.type.id}`, editTypeDialog.value.type)
        
        // Update local data
        const index = meetingTypes.value.findIndex(t => t.id === editTypeDialog.value.type.id)
        if (index !== -1) {
          meetingTypes.value[index] = response.data.meeting_type
        }
        
        editTypeDialog.value.show = false
        showSuccess('Meeting type updated successfully')
      } catch (error) {
        console.error('Error updating meeting type:', error)
        showError('Failed to update meeting type')
      } finally {
        updatingMeetingType.value = false
      }
    }

    const deleteMeetingType = (type) => {
      deleteTypeDialog.value = {
        show: true,
        type
      }
    }

    const confirmDeleteMeetingType = async () => {
      deletingMeetingType.value = true
      try {
        await api.delete(`/meetings/types/${deleteTypeDialog.value.type.id}`)
        
        // Remove from local data
        meetingTypes.value = meetingTypes.value.filter(t => t.id !== deleteTypeDialog.value.type.id)
        
        deleteTypeDialog.value.show = false
        showSuccess('Meeting type deleted successfully')
      } catch (error) {
        console.error('Error deleting meeting type:', error)
        showError('Failed to delete meeting type')
      } finally {
        deletingMeetingType.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      fetchMeetings()
      fetchMeetingTypes()
    })

    return {
      // Data
      loading,
      meetings,
      meetingTypes,
      searchQuery,
      statusFilter,
      typeFilter,
      headers,
      statusOptions,
      statusDialog,
      deleteDialog,

      // Meeting Types Management
      isAdmin,
      showMeetingTypesDialog,
      meetingTypeFormValid,
      editTypeFormValid,
      addingMeetingType,
      updatingMeetingType,
      deletingMeetingType,
      newMeetingType,
      editTypeDialog,
      deleteTypeDialog,
      nameRules,

      // Computed
      filteredMeetings,

      // Methods
      clearFilters,
      formatDate,
      formatTime,
      getStatusColor,
      getStatusIcon,
      viewMeeting,
      editMeeting,
      startMeeting,
      endMeeting,
      cancelMeeting,
      confirmStatusUpdate,
      duplicateMeeting,
      deleteMeeting,
      confirmDelete,

      // Meeting Types Methods
      addMeetingType,
      editMeetingType,
      updateMeetingType,
      deleteMeetingType,
      confirmDeleteMeetingType
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.meetings-page {
  .v-data-table {
    .v-data-table__td {
      padding: 12px 16px;
    }
  }

  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
    
    &:hover {
      text-decoration: underline !important;
    }
  }

  .text-medium-emphasis {
    opacity: 0.7;
  }

  // Meeting Types Management Styles
  .meeting-type-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    margin-bottom: 8px;
    
    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }

  .meeting-type-color {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    margin-right: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
  }
}
</style>